/* eslint-disable @typescript-eslint/no-unused-vars */
import { ThemeOptions } from './options';
import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { Theme } from '@mui/material/styles';
import { outlinedInputClasses, svgIconClasses } from '@mui/material';
import { pickersOutlinedInputClasses } from '@mui/x-date-pickers/PickersTextField';

const red = {
  main: '#EF3333',
  light: '#FF6666',
  dark: '#D82828',
  darker: '#BB1F1F',
};
const orange = {
  main: '#FF6B35',
  light: '#FF8A5B',
  dark: '#E85D31',
  darker: '#D1522C',
};
const cyan = {
  main: '#34C4D7',
  light: '#6DDFEF',
  dark: '#17BCD2',
  darker: '#07AABF',
};
const lightCyan = {
  main: '#C6F5FB',
  light: '#DFF6F9',
  dark: '#A4E7F0',
  darker: '#8DDBE5',
};
const blue = {
  main: '#0685E2',
  light: '#36A3F3',
  dark: '#1073BC',
  darker: '#0860A0',
};
const purple = {
  main: '#9356E0',
  light: '#B680FA',
  dark: '#6F2DC2',
  darker: '#5517A3',
};
const green = {
  main: '#41CA00',
  light: '#64D72E',
  dark: '#44A616',
  darker: '#388812',
};
const yellow = {
  main: '#E7C200',
  light: '#F3D63B',
  dark: '#DEBB00',
  darker: '#C3A402',
};
const white = {
  main: '#ECF4F9',
  light: '#FBFDFE',
  dark: '#E4ECF1',
  darker: '#D2DBE1',
};
const lightGrey = {
  main: '#ACB4B7',
  light: '#C6CDCF',
  dark: '#9CA4A7',
  darker: '#505153',
};
const darkGrey = {
  main: '#4D4E50',
  light: '#3F4142',
  dark: '#2D2E2F',
  darker: '#181819',
};
const blueGrey = {
  main: '#E4ECF1',
  light: '#EFF3F5',
  dark: '#AFC0C9',
  darker: '#607B8A',
};
const black = {
  main: '#000000',
  light: '#333333',
  dark: '#000000',
  darker: '#000000',
};

const shape = {
  borderRadius: 2,
};

function createOutlinedInputStyleOverries<
  Classes extends typeof outlinedInputClasses | typeof pickersOutlinedInputClasses,
>(theme: Theme, classes: Classes) {
  const borderRadius = shape.borderRadius * 4;
  return {
    root: {
      [`& .${svgIconClasses.root}`]: {
        color: theme.palette.tertiary.dark,
      },
      [`&:hover .${classes.notchedOutline}, &.${classes.focused}:hover .${classes.notchedOutline}`]:
        {
          borderColor: theme.palette.tertiary.dark,
        },
      [`&.${classes.disabled} .${classes.notchedOutline}`]: {
        borderColor: theme.palette.tertiary.main,
      },
    },
    input: {
      fontSize: '16px',
      borderRadius,
      '&::placeholder': {
        color: theme.palette.text.secondary,
      },
    },
    notchedOutline: {
      borderRadius,
      borderColor: theme.palette.tertiary.main,
      transition: theme.transitions.create(['border-color']),
    },
  };
}

export const wolfoTheme: ThemeOptions = {
  typography: {
    fontFamily: 'Inter, sans-serif',
    allVariants: {
      letterSpacing: 'normal',
    },
    h1: {
      fontSize: '48px',
      lineHeight: '54px',
      fontWeight: 700,
    },
    h2: {
      fontSize: '32px',
      lineHeight: '40px',
      fontWeight: 700,
    },
    h3: {
      fontSize: '44px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '40px',
      letterSpacing: '-2.2px',
    },
    h4: {
      fontSize: '20px',
      lineHeight: '28px',
    },
    h5: {
      fontSize: '18px',
      lineHeight: '24px',
    },
    h6: {
      fontSize: '16px',
      lineHeight: '24px',
    },
    body1: {
      fontSize: '14px',
      lineHeight: '20px',
    },
    caption: {
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
  createPalette: (mode) => {
    const lightDark = (light: string, dark: string) => (mode === 'light' ? light : dark);

    return {
      mode,
      primary: {
        main: lightDark(orange.main, orange.light),
        light: lightDark(orange.light, orange.main),
        dark: lightDark(orange.dark, orange.darker),
        contrastText: lightDark(white.main, black.darker),
      },
      secondary: {
        main: lightDark(blueGrey.main, darkGrey.dark),
        light: lightDark(blueGrey.light, darkGrey.light),
        dark: lightDark(blueGrey.dark, darkGrey.light),
        contrastText: lightDark(black.main, white.main),
      },
      info: { main: cyan.main, light: cyan.main, dark: cyan.main },
      error: { main: red.main, light: red.main, dark: red.main },
      success: { main: green.main, light: green.main, dark: green.main },
      warning: { main: yellow.main, light: yellow.main, dark: yellow.main },
      tertiary: {
        main: lightDark(blueGrey.dark, blueGrey.darker),
        light: lightDark(blueGrey.light, blueGrey.darker),
        dark: lightDark(blueGrey.darker, blueGrey.darker),
        contrastText: lightDark(white.main, black.main),
      },
      purple: {
        main: purple.main,
        light: purple.light,
        dark: purple.dark,
        contrastText: purple.darker,
      },
      text: {
        primary: lightDark(black.main, white.main),
        secondary: lightDark(blueGrey.darker, lightGrey.dark),
        disabled: lightDark(black.darker, white.darker),
        info: lightDark(cyan.main, cyan.main),
        success: lightDark(green.main, green.main),
      },
      background: {
        default: lightDark(white.light, black.main),
        paper: lightDark('#FFFFFF', darkGrey.darker),
      },
      grey: {
        [400]: lightDark(blueGrey.light, lightGrey.darker),
      },
      surface: {
        primary: lightDark('#FFFFFF', darkGrey.darker),
        secondary: lightDark(blueGrey.light, darkGrey.dark),
        tertiary: lightDark(blueGrey.dark, blueGrey.darker),
        secondaryAlternative: '#0000000D',
      },
    };
  },
  createComponents: (theme) => ({
    MuiOutlinedInput: {
      defaultProps: {
        color: 'tertiary',
      },
      styleOverrides: createOutlinedInputStyleOverries(theme, outlinedInputClasses),
    },
    MuiPickersOutlinedInput: {
      defaultProps: {
        color: 'tertiary',
      },
      styleOverrides: createOutlinedInputStyleOverries(theme, pickersOutlinedInputClasses),
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: 0.7,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
          padding: theme.spacing(2, 4),
          fontSize: '14px',
          lineHeight: '16px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            fontSize: '12px',
            lineHeight: '16px',
            borderRadius: Number(theme.shape.borderRadius ?? 0),
            padding: theme.spacing(2, 3),
          },
        },
        {
          props: { size: 'medium' },
          style: {
            fontSize: '14px',
            lineHeight: '20px',
            borderRadius: Number(theme.shape.borderRadius ?? 0),
            padding: theme.spacing(2.5, 4),
          },
        },
        {
          props: { size: 'large' },
          style: {
            fontSize: '18px',
            lineHeight: '24px',
            borderRadius: Number(theme.shape.borderRadius ?? 0) * 2,
            padding: theme.spacing(4, 6),
          },
        },
        {
          props: { variant: 'text' },
          style: {
            padding: 0,
            minWidth: 'unset',

            '&:hover': {
              opacity: 0.8,
            },
            '&.MuiButton-root': {
              lineHeight: 1.2,
              textDecorationLine: 'underline',
              textDecorationStyle: 'dashed',
              textDecorationThickness: 1,
              textDecorationColor: 'inherit',
              textUnderlineOffset: 2,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            },
          },
        },
        {
          props: { variant: 'text', color: 'secondary' },
          style: {
            color: theme.palette.text.secondary,
          },
        },
        {
          props: { color: 'primary', variant: 'contained' },
          style: {
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
            },
          },
        },
        {
          props: { color: 'secondary', variant: 'contained' },
          style: {
            backgroundColor: theme.palette.secondary.light,
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: 'unset',
          height: 'fit-content',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: Number(theme.shape.borderRadius ?? 0) * 2,
          boxShadow:
            theme.palette.mode === 'light'
              ? '0px 2px 4px 0px rgba(121, 153, 171, 0.12), 0px 1px 1px 0px rgba(121, 153, 171, 0.12)'
              : 'none',
        },
      },
    },
  }),
};
